import { createSlice } from '@reduxjs/toolkit';
import moment from 'moment';
import {
  changePassword,
  fetchMyClients,
  getAccount,
  getGlobalNotifications,
  getTimelineLogs,
  setNotificationTapped,
  updateContactPerson,
  updateDevice,
  updateProfile,
  updateProfileInfo,
  uploadLogo,
} from './accountThunks';

const InitialYear = 2017;
const CurrentYear = Number(new Date().getFullYear());
const CurrentMonth = Number(new Date().getMonth());
const YearGap = (CurrentMonth < 3 ? CurrentYear : CurrentYear + 1) - InitialYear;
const CURRENT_YEAR = {
  selectedFinYear: '',
  selectedFulYear: '',
  currentYear: '',
  previousFinYear: '',
};

function getFinYear() {
  const YEARS = [];

  for (let i = 0; i < YearGap; i++) {
    const start = InitialYear + i;
    const end = InitialYear + i + 1;
    const OBJ = {
      label: `Apr ${start} to Mar ${end}`,
      value: {
        fromDate: `${start}-03-31T18:30:00.000Z`,
        toDate: `${end}-03-31T18:29:59.000Z`,
      },
    };

    if (i === YearGap - 1) {
      CURRENT_YEAR['previousFinYear'] = OBJ;
    }

    if (i + 1 === YearGap) {
      CURRENT_YEAR['selectedFinYear'] = OBJ?.value;
      CURRENT_YEAR['currentYear'] = OBJ;
    }

    YEARS.push(OBJ);
  }

  return YEARS;
}
function getFulfilmentYear() {
  const YEARS = [];

  for (let i = 0; i < YearGap; i++) {
    const start = InitialYear + i;
    const end = InitialYear + i + 1;
    const OBJ = {
      label: `FY ${start} - ${end}`,
      value: {
        fromDate: `${start}-03-31T18:30:00.000Z`,
        toDate: `${end}-03-31T18:29:59.000Z`,
      },
    };

    if (i === YearGap - 1) {
      CURRENT_YEAR['previousFinYear'] = OBJ;
    }

    if (i + 1 === YearGap) {
      CURRENT_YEAR['selectedFulYear'] = OBJ.value;
      CURRENT_YEAR['currentYear'] = OBJ;
    }

    YEARS.push(OBJ);
  }

  // Temporary change for EP-14939:
  // Add FY 2027–28 as an extra fulfilment year option.
  // Remove this temporary addition when the ticket requirement expires.
  const nextFulfilmentYear = {
    label: 'FY 2027 - 2028',
    value: {
      fromDate: '2027-03-31T18:30:00.000Z',
      toDate: '2028-03-31T18:29:59.000Z',
    },
  };

  if (!YEARS.some((year) => year.label === nextFulfilmentYear.label)) {
    YEARS.push(nextFulfilmentYear);
  }

  return YEARS;
}

const initialState = {
  isAuthenticate: false,
  user: null,
  profile: null,
  device: {},
  updateProfileError: null,
  updateContactPersonError: null,
  isLoading: false,
  uploadLogoError: null,
  error: null,
  financialYears: getFinYear(),
  fulfilmentYears: getFulfilmentYear(),
  isUsingFulfilment: localStorage.getItem('isUsingFulfilment')
    ? eval(localStorage.getItem('isUsingFulfilment'))
    : true,
  selectedFinancialYear: CURRENT_YEAR.selectedFinYear,
  selectedFulfilmentYear: CURRENT_YEAR.selectedFulYear,
  currentYear: CURRENT_YEAR.currentYear,
  previousFinancialYear: CURRENT_YEAR.previousFinYear,
  clients: [],
  isLoadingClients: false,
  globalNotifications: [],
  notiLoading: true,
  timelineEvents: [],
  timelineEventsLoading: true,
};
console.log('initial state is', CURRENT_YEAR, initialState);
export const AccountSlice = createSlice({
  name: 'account',
  initialState,
  reducers: {
    setFinancialYear: (state, action) => {
      state.selectedFinancialYear = action.payload;
    },
    setFulfilmentYear: (state, action) => {
      state.selectedFulfilmentYear = action.payload;
    },
    setIsUsingFulfilment: (state, action) => {
      state.isUsingFulfilment = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAccount.pending, (state) => {
        state.isLoading = true;
        state.error = false;
      })
      .addCase(getAccount.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticate = true;
        state.user = action.payload;
        state.error = false;
      })
      .addCase(getAccount.rejected, (state) => {
        state.isLoading = false;
        state.isAuthenticate = false;
        state.error = 500;
      })
      .addCase(updateDevice.fulfilled, (state, action) => {
        state.isLoading = false;
        state.device = action.payload;
      })
      .addCase(fetchMyClients.pending, (state) => {
        state.isLoadingClients = true;
      })
      .addCase(fetchMyClients.fulfilled, (state, action) => {
        state.isLoadingClients = false;
        state.clients =
          action.payload?.map(
            ({ id, partyId, registeredName, businessName, businessCategory, ...restValues }) => ({
              id,
              partyId,
              registeredName,
              businessName,
              businessCategory,
              finalName: businessName || registeredName,
              ...restValues,
            })
          ) || [];
      })
      .addCase(fetchMyClients.rejected, (state) => {
        state.isLoadingClients = false;
      })
      .addCase(updateProfileInfo.pending, (state) => {
        state.isLoading = true;
        state.updateProfileError = null;
      })
      .addCase(updateProfileInfo.fulfilled, (state, action) => {
        state.isLoading = false;
        state.updateProfileError = null;
        state.user = action.payload;
      })
      .addCase(updateProfileInfo.rejected, (state) => {
        state.isLoading = false;
        state.updateProfileError = 500;
      })
      .addCase(updateContactPerson.pending, (state) => {
        state.isLoading = true;
        state.updateContactPersonError = null;
      })
      .addCase(updateContactPerson.fulfilled, (state, action) => {
        state.isLoading = false;
        state.updateContactPersonError = null;
        state.user = action.payload;
      })
      .addCase(updateContactPerson.rejected, (state) => {
        state.isLoading = false;
        state.updateContactPersonError = 500;
      })
      .addCase(uploadLogo.pending, (state) => {
        state.isLoading = true;
        state.uploadLogoError = null;
      })
      .addCase(uploadLogo.fulfilled, (state) => {
        state.isLoading = false;
        state.uploadLogoError = null;
      })
      .addCase(uploadLogo.rejected, (state) => {
        state.isLoading = false;
        state.uploadLogoError = 500;
      })
      .addCase(changePassword.pending, (state) => {
        state.isLoading = true;
        state.uploadLogoError = null;
      })
      .addCase(changePassword.fulfilled, (state) => {
        state.isLoading = false;
        state.uploadLogoError = null;
      })
      .addCase(changePassword.rejected, (state) => {
        state.isLoading = false;
        state.uploadLogoError = 500;
      })
      .addCase(updateProfile.pending, (state) => {
        state.isLoading = true;
        state.uploadLogoError = null;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.uploadLogoError = null;
        state.profile = action.payload;
      })
      .addCase(updateProfile.rejected, (state) => {
        state.isLoading = false;
        state.uploadLogoError = 500;
      })
      .addCase(getGlobalNotifications.pending, (state) => {
        state.notiLoading = true;
      })
      .addCase(getGlobalNotifications.fulfilled, (state, action) => {
        state.notiLoading = false;
        state.globalNotifications = action.payload;
      })
      .addCase(getGlobalNotifications.rejected, (state) => {
        state.notiLoading = false;
      })
      .addCase(setNotificationTapped.fulfilled, (state, action) => {
        const newNotifications = state.globalNotifications?.map((not) => {
          if (not.deliveryLog && not.deliveryLog.id === action.payload) {
            not.deliveryLog.tappedAt = moment().toISOString();
          }
          return not;
        });
        state.globalNotifications = newNotifications;
      })
      .addCase(getTimelineLogs.pending, (state) => {
        state.timelineEventsLoading = true;
      })
      .addCase(getTimelineLogs.fulfilled, (state, action) => {
        state.timelineEventsLoading = false;
        state.timelineEvents = action.payload;
      })
      .addCase(getTimelineLogs.rejected, (state) => {
        state.timelineEvents = false;
      });
  },
});

export const { setFinancialYear, setFulfilmentYear, setIsUsingFulfilment } = AccountSlice.actions;
export const globalNotifications = (state) => state.account.globalNotifications;

export default AccountSlice.reducer;
